import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'

export function useInviteCode(teamId?: string) {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async (data: { expires_in_days?: number; max_uses?: number }) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.post('/users/invite-code', {
        team_id: teamId,
        ...data
      })
      return response.data
    },
  })
}

export function useJoinTeam() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (inviteCode: string) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.post('/users/join-team', {
        invite_code: inviteCode
      })
      return response.data
    },
    onSuccess: () => {
      // Invalidate team queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      queryClient.invalidateQueries({ queryKey: ['team-users'] })
    },
  })
}

export function usePendingMembers(teamId?: string) {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: ['pending-members', teamId],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get(`/users/pending/${teamId}`)
      return response.data
    },
    enabled: !!teamId,
  })
}

export function useMembershipAction() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ membershipId, action }: { membershipId: string; action: 'approve' | 'reject' }) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.patch(`/users/membership/${membershipId}`, { action })
      return response.data
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['pending-members'] })
      queryClient.invalidateQueries({ queryKey: ['team-users'] })
    },
  })
}

export function useRemoveMember() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (membershipId: string) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.delete(`/users/membership/${membershipId}`)
      return response.data
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['team-users'] })
    },
  })
}
